'use client';

import { ActionButton } from '@/components/ui/ActionButton';
import { Plan } from '@/types/common';
import { isPediatricsEligible } from '@/utils/pediatrics';
import Image from 'next/image';
import { useCallback, useMemo } from 'react';
import { useWizardContext } from '../../hooks/useWizardContext';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';

/**
 * PediatricsUpsell step - offers pediatrics upgrade for eligible members.
 * Loops through each eligible member (age 21 or under) when location has pediatrics.
 */
export function PediatricsUpsellStep() {
  const { state, dispatch } = useWizardContext();
  const { goToNextStep } = useWizardNavigation();

  // Get eligible members (age 21 or under)
  const eligibleMembers = useMemo(() => {
    return (state.formData.members || [])
      .map((member, index) => ({ member, index }))
      .filter(({ member }) => {
        if (!member.date_of_birth) return false;
        return isPediatricsEligible(member.date_of_birth);
      });
  }, [state.formData.members]);

  const handleUpgrade = useCallback(() => {
    // Update member with pediatrics_enabled = true
    const updatedMembers =
      state.formData.members?.map(m => {
        const isPeds =
          Boolean(m.date_of_birth) && isPediatricsEligible(m.date_of_birth);

        return {
          ...m,
          pediatrics_enabled: isPeds,
          plan: isPeds ? ('Pediatrics' as Plan) : m.plan,
        };
      }) ?? [];

    dispatch({
      type: 'SET_FORM_DATA',
      payload: { members: updatedMembers },
    });

    goToNextStep();
  }, [state.formData.members, dispatch, goToNextStep]);

  const handleSkip = useCallback(() => {
    goToNextStep();
  }, [goToNextStep]);

  // Shouldn't happen but handle gracefully
  if (!eligibleMembers.length) {
    goToNextStep();
    return null;
  }

  return (
    <div className='mx-auto w-full max-w-[800px] px-4 py-8 xl:max-w-[1200px]'>
      <div className='flex flex-col gap-[40px]'>
        <div className='flex flex-col items-center gap-[24px] text-center'>
          <h1 className='font-serif text-[27px] leading-[115%] font-light text-[#292722]'>
            Unlock access to Sollis Pediatrics.
          </h1>
          <p className='font-sans text-[18px] leading-[130%] font-normal text-[#292722]'>
            World-class primary, urgent, and emergency pediatrics, led by our LA
            and Palm Beach Pediatricians.
          </p>
        </div>

        {/* Marketing card */}
        <div className='flex flex-col items-start gap-[16px] rounded-[16px] bg-[#F1E6D6] p-[16px] xl:flex-row xl:items-center xl:gap-[30px] xl:rounded-[60px] xl:p-[30px] xl:pl-[50px]'>
          {/* Features column */}
          <div className='flex flex-col items-start gap-[16px] xl:flex-1 xl:gap-[30px]'>
            <h3 className='font-sans text-[24px] leading-[100%] font-medium tracking-[-0.02em] text-[#292722] xl:text-[32px] xl:leading-[130%] xl:tracking-[-0.04em]'>
              Sollis Pediatrics starts at
            </h3>
            <p className='font-sans text-[32px] leading-[100%] font-medium tracking-[-0.02em] text-[#292722] xl:text-[60px] xl:leading-[72px] xl:tracking-[-0.04em]'>
              $6,000 / year
            </p>

            {/* Feature list */}
            <div className='flex flex-col items-start gap-[16px] xl:gap-[30px]'>
              {[
                'A dedicated Concierge Pediatrician',
                'Unlimited urgent and emergency care',
                '24/7 virtual care',
                'Camp and school physicals, well visits, vaccines, allergy testing, and more.',
                '1 complimentary house call per year',
              ].map((text, i) => (
                <div
                  key={i}
                  className='flex items-center gap-[16px] xl:gap-[39px]'
                >
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    className='shrink-0 xl:h-[27px] xl:w-[27px]'
                  >
                    <mask id={`check-mask-${i}`}>
                      <circle cx='8' cy='8' r='8' fill='white' />
                      <path
                        d='M4.5 8.5L7 11L11.5 5.5'
                        stroke='black'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </mask>
                    <circle
                      cx='8'
                      cy='8'
                      r='8'
                      fill='#292722'
                      mask={`url(#check-mask-${i})`}
                    />
                  </svg>
                  <span className='font-sans text-[18px] leading-[130%] font-normal text-[#292722] xl:text-[21px]'>
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <Image
            src='/pediatrics-stock-photo.jpg'
            alt='Pediatric care at Sollis Health'
            width={343}
            height={564}
            className='order-2 h-[200px] w-full self-stretch rounded-[40px] object-cover xl:h-auto xl:flex-1'
          />

          {/* Testimonial */}
          <div className='order-1 flex flex-col items-center gap-[30px] self-stretch rounded-[16px] bg-[#F7F5EF] p-[24px] xl:order-2 xl:flex-1 xl:justify-center xl:self-stretch xl:rounded-[60px] xl:p-[48px]'>
            <p className='font-serif text-[27px] leading-[115%] font-light text-[#292722] xl:text-[36px] xl:leading-[130%]'>
              &ldquo;Having immediate access to pediatric care whenever we need
              it has given us real peace of mind.&rdquo;
            </p>
            <p className='self-stretch font-sans text-[18px] leading-[130%] font-bold text-[#292722] xl:text-[21px]'>
              Jerry, parent of Sollis Pediatrics Member
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className='flex flex-col gap-[24px] xl:w-[800px] xl:self-center'>
          <ActionButton
            variant='primary'
            label='Upgrade to Sollis Pediatrics'
            onClick={handleUpgrade}
          />
          <ActionButton
            variant='secondary'
            label='Continue without Sollis Pediatrics'
            onClick={handleSkip}
          />
        </div>
      </div>
    </div>
  );
}
